import { createSlice } from "@reduxjs/toolkit";
// ⚠️ All three deep-import the DESKTOP variant on purpose.
//   - `userLogout` / `supportLogout` exist ONLY in LoginSlice/index.desktop.ts — hazard 6, a
//     bare folder import fails loudly here (TS2614).
//   - `arrayToMap`, `itemsMidMapForBarcode` and `populateOptionSetVariantWrapper` are the QUIET
//     form, hazard 16: all three exist in BOTH catalogueHelpers variants with different bodies
//     (`arrayToMap` returns null vs {}; `itemsMidMapForBarcode` builds a different key), so a
//     bare folder import would silently typecheck against `.native`.
import { supportLogout, userLogout } from "../LoginSlice";
import {
  arrayToMap,
  itemsMidMapForBarcode,
  populateOptionSetVariantWrapper,
} from "../../../helpers/catalogueHelpers";
import { sharedInitialState, sharedReducers } from "./shared";
// `import type`, not a value import: SharedItemsState is an interface, used only by the
// `extends` below. Node's --experimental-transform-types strips annotations per-file without
// cross-file analysis, so a plain named import of a type SURVIVES into the emitted JS and then
// throws "does not provide an export named" at load. Bundlers elide it, which is why this was
// invisible until a test imported through this module.
import type { SharedItemsState } from "./shared";

export type ChannelCategoryMapType = {
  [channelId: string]: {
    [catId: string]: {
      subCats: { [subCatId: string]: {
        items: string[] // List of subcategory item IDs
      } },
      items: string[], // List of all item IDs (direct + subcat items)
      parentCatItems: string[], // List of direct item IDs
    }[]
  }
}

export type MenuChannelMapType = {
  [menuId: string]: ChannelCategoryMapType;
}

export interface ItemsState extends SharedItemsState {
  isCatalogueRefetch: boolean;
  channelToItemMap: ChannelCategoryMapType;
  menuChannelMap: MenuChannelMapType;
  updatedChannelItemsData: any[],
  channelItemsMidBarcodeMapping: {
    [key: string]: any
  },
  optionMapData: Record<string, any>,
  selectedChannelConfigCache: Record<string, any>,
}

const initialState: ItemsState = {
  ...sharedInitialState,
  isCatalogueRefetch: false,
  channelToItemMap: {},
  menuChannelMap: {},
  channelItemsMidBarcodeMapping: {},
  updatedChannelItemsData: [],
  optionMapData: {},
  selectedChannelConfigCache: {},
};

export const itemsSlice = createSlice({
  name: "catalogue",
  initialState,
  reducers: {
    ...sharedReducers,
    // ── `getItemsDataSuccess` diverges: web keeps the raw list plus a mid-map and derives the
    //    channel/menu projections later, through the 12 web-only reducers below. Mobile parses
    //    everything eagerly here instead. Same action, different derived state.
    getItemsDataSuccess: (state, action) => {
      state.allItemsList = action.payload;
      state.itemsData = arrayToMap(action.payload, 'mid');
      state.isItemsFetching = false;
    },
    // ── The 12 below are web-only; mobile's only extra reducer is `resetItemSlice`.
    itemsDataCallComplete: (state) => {
      // this action is to tell that the items data call has completed
    },
    updateItemSlice: (state, action) => {
      return action.payload;
    },
    setCatalogueRefetch: (state, action) => {
      state.isCatalogueRefetch = action.payload;
    },
    setUpdatedItemsData: (state, action) => {
      state.updatedChannelItemsData = arrayToMap(action.payload, 'mid');
      state.channelItemsMidBarcodeMapping = itemsMidMapForBarcode(action.payload);
    },
    updateChannelToItemMap: (state, action) => {
      state.channelToItemMap = action.payload.createNew ? {
        [action.payload.channelId]: action.payload.catItemMap
      } : {
        ...state.channelToItemMap,
        [action.payload.channelId]: action.payload.catItemMap
      };
    },
    clearChannelToItemMap: (state) => {
      state.channelToItemMap = {};
    },
    updateMenuChannelMap: (state, action) => {
      state.menuChannelMap = {
        ...state.menuChannelMap,
        [action.payload.menuId]: {
          ...(state.menuChannelMap[action.payload.menuId] || {}),
          [action.payload.channelId]: action.payload.catItemMap
        }
      };
    },
    clearMenuChannelMap: (state) => {
      state.menuChannelMap = {};
    },
    getOptionMapData: (state, action) => {
      state.optionMapData = populateOptionSetVariantWrapper(action.payload);
    },
    clearOptionMapData: (state) => {
      state.optionMapData = {};
    },
    setSelectedChannelConfig: (state, action) => {
      const { key, data } = action.payload;
      state.selectedChannelConfigCache = {
        ...state.selectedChannelConfigCache,
        [key]: data,
      };
    },
    clearSelectedChannelConfigCache: (state) => {
      state.selectedChannelConfigCache = {};
    },
  },
  // ⚠️ WEB-ONLY. Mobile's ItemsSlice declares no extraReducers at all — it resets through its
  // own `resetItemSlice` action instead of listening for the logout actions.
  extraReducers: (builder) => {
    builder
      .addCase(userLogout, (state) => {
        state.channelToItemMap = {};
        state.menuChannelMap = {};
        state.optionMapData = {};
        state.selectedChannelConfigCache = {};
        // resetting items related data on logout
        state.itemsData = {};
        state.isItemsFetching = false;
        state.channelItemsMidBarcodeMapping = {};
        state.allItemsList = [];
      })
      .addCase(supportLogout, (state) => {
        state.channelToItemMap = {};
        state.menuChannelMap = {};
        state.optionMapData = {};
        state.selectedChannelConfigCache = {};
      });
  },
});

export const {
  getItemsData,
  getItemsDataSuccess,
  getItemsDataFail,
  updateItemSlice,
  setCatalogueRefetch,
  setUpdatedItemsData,
  updateChannelToItemMap,
  clearChannelToItemMap,
  updateMenuChannelMap,
  clearMenuChannelMap,
  itemsDataCallComplete,
  getOptionMapData,
  clearOptionMapData,
  setSelectedChannelConfig,
  clearSelectedChannelConfigCache,
} = itemsSlice.actions;
export default itemsSlice.reducer;
